import { useId } from 'react';
import { cx, type Hue } from './shared';

export interface PaginationProps {
  /** Rows in the whole list. */
  total: number;
  /** Rows per page. */
  limit: number;
  /** Index of the first row on this page. */
  offset: number;
  /** Called with the new offset when a page is chosen. */
  onChange: (offset: number) => void;
  /** Page sizes to choose from; omit to hide the size picker. */
  pageSizes?: number[];
  onLimitChange?: (limit: number) => void;
  /** The total is an estimate: the summary says "about". */
  approximate?: boolean;
  /** Pages shown either side of the current one. Default 1. */
  siblings?: number;
  /** Hide the "1–50 of 1,234" summary. */
  hideSummary?: boolean;
  hue?: Hue;
  size?: 'sm' | 'base';
  /** Push everything to the end of the row. */
  end?: boolean;
  className?: string;
  'aria-label'?: string;
}

/** Page numbers to show: the first, the last, and `siblings` either side of the current, with gaps. */
export function pageWindow(current: number, pages: number, siblings = 1): Array<number | 'gap'> {
  const shown = new Set<number>([1, pages]);
  for (let p = current - siblings; p <= current + siblings; p++) if (p >= 1 && p <= pages) shown.add(p);
  const sorted = [...shown].sort((a, b) => a - b);
  const out: Array<number | 'gap'> = [];
  sorted.forEach((p, i) => {
    const prev = sorted[i - 1];
    if (prev !== undefined && p - prev === 2) out.push(prev + 1); // fill a gap of one page rather than "…"
    else if (prev !== undefined && p - prev > 2) out.push('gap');
    out.push(p);
  });
  return out;
}

const number = new Intl.NumberFormat();

/** Page buttons for a server-paged list (limit / offset). */
export function Pagination({
  total,
  limit,
  offset,
  onChange,
  pageSizes,
  onLimitChange,
  approximate,
  siblings = 1,
  hideSummary,
  hue,
  size,
  end,
  className,
  'aria-label': label = 'Pagination',
}: PaginationProps) {
  const sizeId = useId();
  const pages = Math.max(1, Math.ceil(total / limit));
  const current = Math.min(pages, Math.floor(offset / limit) + 1);
  const go = (page: number) => onChange((Math.min(Math.max(page, 1), pages) - 1) * limit);
  const first = total === 0 ? 0 : offset + 1;
  const last = Math.min(offset + limit, total);

  return (
    <nav
      className={cx('pagination', hue && `pagination--${hue}`, size === 'sm' && 'pagination--sm', end && 'pagination--end', className)}
      aria-label={label}
    >
      {!hideSummary && (
        <span className="pagination__summary">
          {first.toLocaleString()}–{last.toLocaleString()} of {approximate && 'about '}
          {number.format(total)}
        </span>
      )}
      {pages > 1 && (
        <ol className="pagination__pages">
          <li>
            <button type="button" className="pagination__item" disabled={current === 1} onClick={() => go(current - 1)} aria-label="Previous page">
              ‹
            </button>
          </li>
          {pageWindow(current, pages, siblings).map((p, i) =>
            p === 'gap' ? (
              <li key={`gap-${i}`}>
                <span className="pagination__gap" aria-hidden="true">
                  …
                </span>
              </li>
            ) : (
              <li key={p}>
                <button
                  type="button"
                  className="pagination__item"
                  aria-current={p === current ? 'page' : undefined}
                  aria-label={`Page ${p}`}
                  onClick={() => p !== current && go(p)}
                >
                  {number.format(p)}
                </button>
              </li>
            ),
          )}
          <li>
            <button type="button" className="pagination__item" disabled={current === pages} onClick={() => go(current + 1)} aria-label="Next page">
              ›
            </button>
          </li>
        </ol>
      )}
      {pageSizes && onLimitChange && (
        <label className="pagination__size" htmlFor={sizeId}>
          Per page
          <select
            id={sizeId}
            className={cx('select', size === 'sm' ? 'select--xs' : 'select--sm')}
            value={limit}
            onChange={(e) => onLimitChange(Number(e.target.value))}
          >
            {pageSizes.map((n) => (
              <option key={n} value={n}>
                {n}
              </option>
            ))}
          </select>
        </label>
      )}
    </nav>
  );
}
